// EvolveDialog.jsx
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
    Box,
    FormControlLabel,
    Checkbox,
    Radio,
    Stack,
} from '@mui/material';
import { useState, useMemo } from 'react';
import { PlayingCard } from '../components/PlayingCard'; // ← adjust path if needed

function EvolveDialog({ open, onClose, card, userCards, onConfirm }) {
    const [selectedId, setSelectedId] = useState(null);
    const [onlyLevel1, setOnlyLevel1] = useState(false);

    // ---- Theme tokens to match project style ----
    const GOLD = '#d4af37';
    const GOLD_SOFT = 'rgba(212,175,55,0.75)';
    const GOLD_DIM = 'rgba(212,175,55,0.6)';
    const BG_DARK = '#0a0a0a';
    const BG_PANEL = 'rgba(0,0,0,0.7)';
    const TEXT_WARM = '#f9f6e3';
    const CINZEL = `'Cinzel', serif`;

    const goldBtnSolid = {
        fontFamily: CINZEL,
        letterSpacing: 0.6,
        color: BG_DARK,
        background: `linear-gradient(180deg, ${GOLD_SOFT}, ${GOLD})`,
        border: `1px solid ${GOLD_DIM}`,
        boxShadow: `0 2px 12px rgba(212,175,55,0.25)`,
        '&:hover': {
            background: `linear-gradient(180deg, ${GOLD}, ${GOLD})`,
            boxShadow: `0 4px 20px rgba(212,175,55,0.35)`,
        },
    };

    const goldBtnOutline = {
        fontFamily: CINZEL,
        letterSpacing: 0.6,
        color: TEXT_WARM,
        border: `1px solid ${GOLD_DIM}`,
        background: 'transparent',
        boxShadow: `inset 0 0 0 1px rgba(212,175,55,0.15)`,
        '&:hover': {
            borderColor: GOLD,
            boxShadow: `0 2px 12px rgba(212,175,55,0.25), inset 0 0 0 1px ${GOLD}`,
            background: 'rgba(212,175,55,0.06)',
        },
    };

    const subtlePanel = {
        background: BG_PANEL,
        border: `1px solid ${GOLD_DIM}`,
        boxShadow: `0 0 24px rgba(212,175,55,0.15)`,
        borderRadius: 2,
    };

    const controlChrome = {
        color: GOLD_DIM,
        '&.Mui-checked': { color: GOLD },
    };

    const labelChrome = {
        color: GOLD_SOFT,
        fontFamily: CINZEL,
        letterSpacing: 0.5,
    };

    // Label helper to match LevelUpDialog look
    const classificationLabel = (cls) => {
        const c = String(cls || '').toLowerCase();
        if (c === 'man') return 'Man';
        if (c === 'beast') return 'Beast';
        return cls || '—';
    };

    // Base candidates: same cardId + same evolution stage, not the same instance
    const baseCandidates = useMemo(
        () =>
            (userCards || []).filter(
                (c) =>
                    c.instanceId !== card.instanceId &&
                    c.cardId === card.cardId &&
                    c.evolutionStage === card.evolutionStage
            ),
        [userCards, card?.instanceId, card?.cardId, card?.evolutionStage]
    );

    const level1Count = useMemo(
        () => baseCandidates.filter((c) => (c.level ?? 1) === 1).length,
        [baseCandidates]
    );

    const candidates = useMemo(
        () =>
            onlyLevel1
                ? baseCandidates.filter((c) => (c.level ?? 1) === 1)
                : baseCandidates,
        [baseCandidates, onlyLevel1]
    );

    const handleConfirm = () => {
        if (selectedId) {
            onConfirm(selectedId);
            setSelectedId(null);
            setOnlyLevel1(false);
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    background: BG_DARK,
                    color: TEXT_WARM,
                    border: `1px solid ${GOLD_DIM}`,
                    boxShadow: `0 0 32px rgba(212,175,55,0.20)`,
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontFamily: CINZEL,
                    letterSpacing: 1,
                    fontWeight: 700,
                    color: GOLD,
                    background: 'linear-gradient(180deg, rgba(0,0,0,0.4), transparent)',
                    borderBottom: `1px solid ${GOLD_DIM}`,
                    pb: 2,
                }}
            >
                Evolve <strong style={{ color: TEXT_WARM }}>{card?.name}</strong>
            </DialogTitle>

            <DialogContent sx={{ pt: 2.5 }}>
                {/* Target card preview (matches LevelUpDialog style) */}
                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 4, // increased from 2 → 4 for spacing
                        ...subtlePanel,
                    }}
                >
                    <Box sx={{ width: 160, flexShrink: 0 }}>
                        <PlayingCard card={card} showLevel={true} showStats={true} />
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            ml: 10,
                            pr: 1,
                            overflow: 'hidden',
                            alignSelf: 'center',
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 700,
                                color: TEXT_WARM,
                                fontFamily: CINZEL,
                                letterSpacing: 0.5,
                                mb: 0.5,
                            }}
                        >
                            {card?.name}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: GOLD_SOFT, mb: 0.5, whiteSpace: 'nowrap' }}
                        >
                            {classificationLabel(card?.classification)} • {card?.rarity}
                        </Typography>
                        <Typography variant="body2" sx={{ color: TEXT_WARM, mb: 0.5 }}>
                            Current Evolution Stage:{' '}
                            <strong>{card?.evolutionStage}</strong>
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: GOLD_SOFT,
                                lineHeight: 1.4,
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                            }}
                        >
                            Select exactly one duplicate with the same evolution stage to evolve.
                        </Typography>
                    </Box>
                </Box>

                {/* Simple toolbar — only Level 1 toggle */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    spacing={1.5}
                    sx={{ mb: 2, p: 1.5, ...subtlePanel }}
                >
                    <FormControlLabel
                        sx={{ color: TEXT_WARM }}
                        control={
                            <Checkbox
                                checked={onlyLevel1}
                                onChange={(e) => setOnlyLevel1(e.target.checked)}
                                sx={controlChrome}
                            />
                        }
                        label={
                            <Typography variant="body2" sx={{ ...labelChrome, color: TEXT_WARM }}>
                                Only show Level 1 ({level1Count})
                            </Typography>
                        }
                    />
                    <Typography
                        variant="body2"
                        sx={{ color: GOLD_SOFT, fontFamily: CINZEL, letterSpacing: 0.5 }}
                    >
                        Available duplicates:{' '}
                        <strong style={{ color: TEXT_WARM }}>{candidates.length}</strong>
                    </Typography>
                </Stack>

                {/* Candidate choices (LevelUpDialog-inspired cards grid) */}
                {candidates.length === 0 ? (
                    <Typography sx={{ color: GOLD_SOFT }}>
                        No valid duplicate cards available for evolution.
                    </Typography>
                ) : (
                    <Grid container spacing={2}>
                        {candidates.map((c) => {
                            const checked = selectedId === c.instanceId;
                            return (
                                <Grid item xs={12} sm={6} md={4} key={c.instanceId}>
                                    <Box
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setSelectedId(c.instanceId)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setSelectedId(c.instanceId);
                                            }
                                        }}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            border: '2px solid',
                                            borderColor: checked ? GOLD : GOLD_DIM,
                                            cursor: 'pointer',
                                            height: '100%',
                                            background: 'rgba(0,0,0,0.6)',
                                            boxShadow: checked
                                                ? `0 0 18px rgba(212,175,55,0.35)`
                                                : `0 0 10px rgba(212,175,55,0.08)`,
                                            transition:
                                                'box-shadow 120ms ease, border-color 120ms ease, transform 80ms ease',
                                            '&:hover': {
                                                borderColor: GOLD,
                                                boxShadow: `0 0 18px rgba(212,175,55,0.25)`,
                                                transform: 'translateY(-1px)',
                                            },
                                        }}
                                    >
                                        <Box sx={{ width: '100%', mb: 1 }}>
                                            <PlayingCard card={c} />
                                        </Box>

                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 700,
                                                color: TEXT_WARM,
                                                fontFamily: CINZEL,
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            {c.name}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: GOLD_SOFT }}>
                                            {classificationLabel(c.classification)} • {c.rarity}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5, color: TEXT_WARM }}>
                                            Level: <strong>{c.level ?? 1}</strong> &nbsp;|&nbsp; Active:{' '}
                                            <strong>{c.active ? 'Yes' : 'No'}</strong>
                                        </Typography>

                                        <FormControlLabel
                                            sx={{ mt: 1, color: TEXT_WARM }}
                                            onClick={(e) => e.stopPropagation()}
                                            control={
                                                <Radio
                                                    checked={checked}
                                                    onChange={() => setSelectedId(c.instanceId)}
                                                    sx={controlChrome}
                                                />
                                            }
                                            label="Use this card"
                                        />
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: `1px solid ${GOLD_DIM}`,
                    background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.5))',
                    py: 1.5,
                }}
            >
                <Typography
                    sx={{ mr: 'auto', pl: 2, color: GOLD_SOFT, fontFamily: CINZEL, letterSpacing: 0.5 }}
                    variant="body2"
                >
                    Selected: <strong style={{ color: TEXT_WARM }}>{selectedId ? 1 : 0}</strong>
                </Typography>
                <Button onClick={onClose} sx={{ ...goldBtnOutline, mr: 1 }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={!selectedId}
                    variant="contained"
                    color="primary"
                    sx={goldBtnSolid}
                >
                    Confirm Evolution
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default EvolveDialog;
